import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "expo-router";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { searchGames, type ApiGame } from "../../src/api";
import { formatDesigners, formatSubtitle } from "../../src/format";
import { CoverNotice, Thumbnail } from "../../src/cover";
import { colour, type } from "../../src/theme";

/**
 * Search, against the real catalogue.
 *
 * The sample data this screen used to render is gone -- WI-012 gave it
 * somewhere to ask.
 */
export default function SearchScreen() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ApiGame[]>([]);
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const inFlight = useRef<AbortController | null>(null);

  const run = useCallback((q: string) => {
    // Abort the previous request rather than racing it. Without this, a slow
    // response for "cat" can land after a fast one for "catan" and overwrite
    // the newer results -- rare locally, common on a phone.
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;

    if (q.trim().length === 0) {
      setResults([]);
      setState("idle");
      setError(null);
      return;
    }

    setState("loading");
    searchGames(q, { limit: 30, signal: controller.signal })
      .then((games) => {
        if (controller.signal.aborted) return;
        setResults(games);
        setState("idle");
        setError(null);
      })
      .catch((cause: Error) => {
        if (controller.signal.aborted || cause.name === "AbortError") return;
        setState("error");
        setError(cause.message);
      });
  }, []);

  useEffect(() => {
    // Debounced: one request per pause, not one per keystroke.
    const timer = setTimeout(() => run(query), 250);
    return () => clearTimeout(timer);
  }, [query, run]);

  return (
    <View style={styles.screen}>
      <TextInput
        style={styles.input}
        placeholder="Search 333 games — try Rosenberg, or worker-placement"
        placeholderTextColor={colour.inkFaint}
        value={query}
        onChangeText={setQuery}
        autoCorrect={false}
        autoCapitalize="none"
        returnKeyType="search"
      />

      {state === "error" && (
        <View style={styles.error}>
          <Text style={styles.errorText}>{error}</Text>
          <Pressable onPress={() => run(query)}>
            <Text style={styles.retry}>Try again</Text>
          </Pressable>
        </View>
      )}

      {state === "loading" && results.length === 0 && (
        <ActivityIndicator style={styles.spinner} color={colour.inkMuted} />
      )}

      {state === "idle" && query.trim().length > 0 && results.length === 0 && (
        <Text style={styles.empty}>Nothing matched “{query.trim()}”.</Text>
      )}

      <CoverNotice shown={results.some((g) => g.imageUrl)} />

      <FlatList
        data={results}
        keyExtractor={(game) => game.slug}
        contentContainerStyle={styles.list}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item }) => (
          <Link href={{ pathname: "/game/[slug]", params: { slug: item.slug } }} asChild>
            <Pressable style={styles.card}>
              <Thumbnail url={item.imageUrl} title={item.title} />
              <View style={styles.cardText}>
                <Text style={styles.title}>{item.title}</Text>
                <Text style={styles.subtitle}>{formatSubtitle(item)}</Text>
                <Text style={styles.designers}>{formatDesigners(item)}</Text>
              </View>
            </Pressable>
          </Link>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, padding: 16 },
  input: {
    backgroundColor: colour.surface,
    borderColor: colour.lineStrong,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    ...type.body,
    color: colour.ink,
    marginBottom: 12,
  },
  spinner: { marginVertical: 24 },
  empty: { color: colour.inkMuted, ...type.meta, marginTop: 12 },
  error: {
    backgroundColor: colour.dangerSurface,
    borderColor: colour.dangerLine,
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    gap: 6,
  },
  errorText: { color: colour.danger, ...type.meta },
  retry: { color: colour.accent, ...type.meta, fontWeight: "600" },
  list: { gap: 12, paddingBottom: 24 },
  cardText: { flex: 1, gap: 2 },
  card: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: colour.surface,
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: colour.line,
  },
  title: { ...type.title, fontWeight: "600", color: colour.ink },
  subtitle: { ...type.meta, color: colour.inkMuted, marginTop: 4 },
  designers: { ...type.meta, color: colour.inkMuted, marginTop: 2 },
});
